import { For, onCleanup, onMount, Canlendar, createSignal, createMemo } from 'cdui-js';
import { CheckBoxCard } from '../../../components/CheckBoxCard';
import { saveApply } from '../../../../../api/open-account';
import { dayts } from '@china-difi/lib/date';
import { lastProcess, nextProcess, pState, getApplySaveExtra, setFormValidateFunc } from '../store';
import { LastNextBtns } from '../components/LastNextBtns';
import { showToast } from '../../../components/Toast';
import i18n from '../../../../../i18n';

const getTimeSlots = () =>
  [
    { label: i18n.OpenProgress.Offline.Slot0900, value: 1 },
    { label: i18n.OpenProgress.Offline.Slot1000, value: 2 },
    { label: i18n.OpenProgress.Offline.Slot1200, value: 3 },
    { label: i18n.OpenProgress.Offline.Slot1400, value: 4 },
    { label: i18n.OpenProgress.Offline.Slot1700, value: 5 },
    { label: i18n.OpenProgress.Offline.Slot1900, value: 6 },
  ] as const;

/** 秒级时间戳，用于与当前时间精确到秒对比 */
const toSecondTimestamp = (date: Date) => Math.floor(date.getTime() / 1000);

const normalizeDateStr = (value: string) => {
  if (!value) return '';
  const [raw] = value.trim().split(/[ T]/);
  const parts = raw.split(/[-/]/);
  if (parts.length !== 3) return value;
  const [year, month, day] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const parseDateParts = (dateStr: string) => {
  const normalized = normalizeDateStr(dateStr);
  const [year, month, day] = normalized.split('-').map((part) => Number(part));
  return { year, month, day };
};

const parseSlotEndTime = (dateStr: string, label: string) => {
  const endText = label.split('~')[1]?.trim() ?? '';
  const [hour, minute] = endText.split(':').map((part) => Number(part));
  const { year, month, day } = parseDateParts(dateStr);
  return new Date(year, month - 1, day, hour, minute || 0, 0);
};

const isTodayDate = (dateStr: string) =>
  normalizeDateStr(dateStr) === dayts.format(Date.now(), 'YYYY-MM-DD');

/** 仅「今天」才按当前时刻禁用已结束时段；未来日期全部可选 */
const isTimeSlotExpired = (dateStr: string, label: string) => {
  const normalized = normalizeDateStr(dateStr);
  if (!normalized) return true;
  if (!isTodayDate(normalized)) return false;
  return toSecondTimestamp(parseSlotEndTime(normalized, label)) <= toSecondTimestamp(new Date());
};

/** 日历：今天之前的日期置灰不可点 */
const isPastCalendarDate = (year: number, month: number, date: number) => {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const cellDate = new Date(year, month - 1, date);
  cellDate.setHours(0, 0, 0, 0);
  return cellDate.getTime() < todayStart.getTime();
};

const getFirstAvailableSlot = (dateStr: string) =>
  getTimeSlots().find((item) => !isTimeSlotExpired(dateStr, item.label))?.label ?? '';

const ensureValidSelection = () => {
  pState.offline.date = normalizeDateStr(pState.offline.date);
  if (!pState.offline.date) {
    pState.offline.date = dayts.format(Date.now() + 24 * 60 * 60 * 1000, 'YYYY-MM-DD');
  } else {
    const { year, month, day } = parseDateParts(pState.offline.date);
    if (isPastCalendarDate(year, month, day)) {
      pState.offline.date = dayts.format(Date.now() + 24 * 60 * 60 * 1000, 'YYYY-MM-DD');
    }
  }

  if (!pState.offline.time || isTimeSlotExpired(pState.offline.date, pState.offline.time)) {
    const available = getFirstAvailableSlot(pState.offline.date);
    if (available) {
      pState.offline.time = available;
    }
  }
};

export const Time = () => {
  const [selectedDate, setSelectedDate] = createSignal(normalizeDateStr(pState.offline.date));

  let calendarWrapRef: HTMLDivElement | undefined;

  const timeSlotItems = createMemo(() => {
    const date = selectedDate();
    return getTimeSlots().map((item) => ({
      ...item,
      expired: isTimeSlotExpired(date, item.label),
    }));
  });

  const syncSelectedDate = (dateStr: string) => {
    const normalized = normalizeDateStr(dateStr);
    setSelectedDate(normalized);
    pState.offline.date = normalized;
    if (!pState.offline.time || isTimeSlotExpired(normalized, pState.offline.time)) {
      const available = getFirstAvailableSlot(normalized);
      if (available) {
        pState.offline.time = available;
      }
    }
  };

  const handleDateChange = (event: Event) => {
    const detail = (event as CustomEvent<Date>).detail;
    if (!detail) return;
    syncSelectedDate(dayts.format(detail.getTime(), 'YYYY-MM-DD'));
  };

  onMount(() => {
    ensureValidSelection();
    setSelectedDate(normalizeDateStr(pState.offline.date));

    setFormValidateFunc({
      callback: async () => {
        if (
          !String(pState.offline.date || '').trim() ||
          !String(pState.offline.time || '').trim() ||
          isTimeSlotExpired(pState.offline.date, pState.offline.time)
        ) {
          showToast(i18n.OpenProgress.Offline.ToastTimeRequired);
          return false;
        }
        return true;
      },
    });

    calendarWrapRef?.addEventListener('change', handleDateChange);
    onCleanup(() => calendarWrapRef?.removeEventListener('change', handleDateChange));
  });

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-XXS">{i18n.OpenProgress.Offline.TimeTitle}</div>
      <div class="color margin-b-L">{i18n.OpenProgress.Offline.TimeDesc}</div>

      <div ref={calendarWrapRef} class="margin-y open-progress-calendar-wrap">
        <Canlendar
          class="open-progress-calendar"
          value={selectedDate()}
          disableFn={isPastCalendarDate}
        />
      </div>

      <div class="color-D margin-b-XS">{i18n.OpenProgress.Offline.TimeSlotLabel}</div>

      <div class="flex flex-wrap gap-8px">
        <For each={timeSlotItems()}>
          {(item) => (
            <CheckBoxCard
              style={{ width: '31%' }}
              class={`padding-x padding-y-S flex justify-center${item.expired ? ' open-progress-time-disabled' : ''}`}
              active={!item.expired && pState.offline.time === item.label}
              onclick={() => {
                if (item.expired) return;
                pState.offline.time = item.label;
              }}
            >
              <div class={item.expired ? 'color-L' : 'color-D'}>{item.label}</div>
            </CheckBoxCard>
          )}
        </For>
      </div>

      <LastNextBtns
        last={() => {
          lastProcess();
        }}
        next={async () => {
          await saveApply({
            progress: 3,
            appointmentTime: pState.offline.date + ' ' + pState.offline.time,
            applyType: 'NEW_APPLY',
            ...getApplySaveExtra(),
          });
          nextProcess();
        }}
      />
    </div>
  );
};
